/* eslint-disable react-hooks/exhaustive-deps */
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Card } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React, { useEffect, useMemo } from "react";

import MDBadge from "@/components/atoms/MDBadge";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import { OrderFormData } from "@/components/screens/OrderFormPage/types";
import { formatCurrency } from "@/helpers/currency-helpers";
import useOrderStatusLogic from "@/hooks/use-order-status-logic";
import { IOrder, IOrderInput, IOrderPayment } from "@/interfaces/order.interfaces";
import { PaymentDataTabMessages } from "@/messages/order-form-page/payment-data-tab.messages";

import { defaultPayment } from "../../../../form-data";
import { getExpectedOrderAmount, getOrderItemTotals } from "../../../ItemsTab/form-data";
import { PaymentsTable } from "../../paymentTable";

type Props = {
  messages: PaymentDataTabMessages;
  order: IOrder | IOrderInput | null;
};

export default function Payments({ messages, order }: Props) {
  const { values, setFieldValue } = useFormikContext<OrderFormData>();
  const { isTalk2BuyChannel } = useOrderStatusLogic(order, values.isEditing);

  const paymentTotal = useMemo(
    () =>
      values.payments.reduce((acc, payment) => {
        const amount = parseFloat(payment.paymentAmount.toString()) || 0;
        return acc + amount;
      }, 0),
    [values.payments]
  );

  const expectedPaymentValue = getExpectedOrderAmount(values);
  const differenceValue = parseFloat((expectedPaymentValue - paymentTotal).toFixed(2));

  useEffect(() => {
    const { netTotal } = getOrderItemTotals(values.items);
    setFieldValue("orderAmount", netTotal);
    setFieldValue("differenceValue", differenceValue);
  }, [values.items, expectedPaymentValue, differenceValue, setFieldValue]);

  const handleAddPaymentMethod = (newMethod: IOrderPayment) => {
    const updatePayments = [...values.payments, newMethod];
    setFieldValue("payments", updatePayments);
  };

  return (
    <Card>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between">
          <MDTypography variant="h5" fontWeight="bold">
            {messages.groupPaymentsTitle}
          </MDTypography>
        </MDBox>

        <Grid
          p={3}
          pl={0}
          pr={0}
          container
          spacing={3}
          alignItems="center"
          justifyContent={"space-between"}
        >
          <Grid container alignItems={"center"} size={{ xs: 12, md: 6, lg: 6 }}>
            <MDBadge
              color="light"
              size="md"
              badgeContent={`${messages.orderAmountLabel}: ${formatCurrency(expectedPaymentValue)}`}
              container
            />

            <MDBadge
              color="light"
              size="md"
              badgeContent={`${messages.paymentTotalAmountLabel}: ${formatCurrency(paymentTotal)}`}
              container
            />

            <MDBadge
              color="light"
              size="md"
              badgeContent={`${messages.differenceAmountLabel}: ${formatCurrency(differenceValue)}`}
              container
            />
          </Grid>

          <MDBox display="flex" justifyContent="flex-end">
            <MDButton
              variant="outlined"
              color="dark"
              disabled={values.disabledButton}
              onClick={() => handleAddPaymentMethod(defaultPayment())}
              endIcon={<AddCircleOutlineIcon />}
            >
              {messages.createButton}
            </MDButton>
          </MDBox>
        </Grid>

        <PaymentsTable messages={messages} isTalk2BuyChannel={!!isTalk2BuyChannel} />
      </MDBox>
    </Card>
  );
}
